import { dateRangeValidator, DATE_RANGE_MESSAGE } from '../../../core/utils/date-range.validator';
import { ibanValidator, normalizeIban } from '../../../core/utils/iban.util';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import * as ClientConstants from '../../../core/constants/client.constants';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { ClientsTableComponent } from '../../clients-table/clients-table.component';
import { CommonModule } from '@angular/common';
import { InputMaskModule } from 'primeng/inputmask';
import { IbanFormatPipe } from "../../../core/pipes/iban-format.pipe";
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';

@Component({
  selector: 'app-other-data-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DropdownModule, InputTextModule, InputTextareaModule, CheckboxModule, CalendarModule, AccordionModule, ClientsTableComponent, InputMaskModule, IbanFormatPipe, AutoAuditDirective],
  templateUrl: './other-data-form.component.html',
  styleUrl: './other-data-form.component.scss'
})
export class OtherDataFormComponent implements OnChanges {

  public clientConstants = ClientConstants;

  @Input() clientId: any;
  @Input() hasEditPermission: boolean;
  @Input() snapshotData: any[] | null = null;

  fieldAuditData: LatestPerFieldMap = {};
  @Output() formIsDirty = new EventEmitter<boolean>();

  otherDataForm = this.fb.group({
    id: [],
    client: [],
    administrationNameAEAT: [''],
    administrationCodeAEAT: [''],
    delegationAEAT: [''],
    intraCommunityOperatorObligation: [''],
    intraCommunityOperatorPeriods: this.fb.array([]),
    presentationSIIObligation: [''],
    presentationSIIPeriods: this.fb.array([]),
    codeLEI: [''],
    codeLEIPeriods: this.fb.array([]),
    codeEORI: [''],
    codeEORIPeriods: this.fb.array([]),
    // observations: ['']
  });

  ibanControl = new FormControl('');


  bankAccountForm = this.fb.group({
    bankAccounts: this.fb.array([]),
  });

  selectedBankAccount: FormGroup | null = null;


  headers = [ 
    {
      name: 'Banco',
      key: 'bankName'
    },
    {
      name: 'IBAN',
      key: 'iban'
    },
    {
      name: 'Fecha alta',
      key: 'startDate'
    },
    {
      name: 'Fecha baja',
      key: 'endDate'
    },
    {
      name: 'Observaciones',
      key: 'observations'
    },
    {
      name: 'Acciones',
      key: 'actions'
    }];

  configTable = {
    headers: this.headers,
    data: undefined
  }

  modalForm: FormGroup = this.fb.group({
    id: [],
    bankName: [''],
    observations: [''],
    iban: ['', [ibanValidator]],
    startDate: [],
    endDate: [],
    client: []

  });

  isModalActive: boolean = false;
  isCreating: boolean = false;

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private auditService: AuditService,
    ) {
      this.modalForm.addValidators(dateRangeValidator());
      this.otherDataForm.valueChanges.subscribe(() => {
        if (this.otherDataForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.bankAccountForm.valueChanges.subscribe(() => {
        if (this.bankAccountForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
     }

  async ngOnInit() {
    if (!this.snapshotData) {
      await this.getData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        this.configTable = { ...this.configTable, data: [...this.snapshotData] };
      } else if (!changes['snapshotData'].firstChange) {
        void this.getData();
      }
    }
  }

  get roiPeriods(): FormArray {
    return this.otherDataForm.get('intraCommunityOperatorPeriods') as FormArray;
  }

  get leiPeriods(): FormArray {
    return this.otherDataForm.get('codeLEIPeriods') as FormArray;
  }

  get eoriPeriods(): FormArray {
    return this.otherDataForm.get('codeEORIPeriods') as FormArray;
  }

  get siiPeriods(): FormArray {
    return this.otherDataForm.get('presentationSIIPeriods') as FormArray;
  }

  async getData() {
    if (this.clientId) {
      let data = await this.clientsService.getClientOtherData(this.clientId);
      
      // Clear existing periods
      this.roiPeriods.clear();
      this.leiPeriods.clear();
      this.eoriPeriods.clear();
      this.siiPeriods.clear();

      // Load ROI periods
      if (data?.intraCommunityOperatorPeriods && data.intraCommunityOperatorPeriods.length > 0) {
        data.intraCommunityOperatorPeriods.forEach((period: any) => {
          this.roiPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load LEI periods
      if (data?.codeLEIPeriods && data.codeLEIPeriods.length > 0) {
        data.codeLEIPeriods.forEach((period: any) => {
          this.leiPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load EORI periods
      if (data?.codeEORIPeriods && data.codeEORIPeriods.length > 0) {
        data.codeEORIPeriods.forEach((period: any) => {
          this.eoriPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      // Load SII periods
      if (data?.presentationSIIPeriods && data.presentationSIIPeriods.length > 0) {
        data.presentationSIIPeriods.forEach((period: any) => {
          this.siiPeriods.push(this.fb.group({
            startDate: period.startDate ? new Date(period.startDate) : null,
            endDate: period.endDate ? new Date(period.endDate) : null
          }));
        });
      }

      let bankAccounts = await this.clientsService.getClientBankAccounts(this.clientId);
      this.bankAccounts.clear();

      if (bankAccounts.length > 0) {
        bankAccounts.forEach(account => {
          let bankAccount = this.fb.group({
            id: account.id,
            bankName: account.bankName,
            iban: account.iban,
            client: account.client,
            startDate: parseDateOnlyToLocal(account.startDate),
            endDate: parseDateOnlyToLocal(account.endDate),
            observations: [account.observations ?? ''],
          });

          this.bankAccounts.push(bankAccount);
        });
      }
      
      this.otherDataForm.patchValue({
        id: data?.id,
        administrationNameAEAT: data?.administrationNameAEAT,
        administrationCodeAEAT: data?.administrationCodeAEAT,
        delegationAEAT: data?.delegationAEAT,
        intraCommunityOperatorObligation: data?.intraCommunityOperatorObligation,
        presentationSIIObligation: data?.presentationSIIObligation,
        codeLEI: data?.codeLEI,
        codeEORI: data?.codeEORI,
        // observations: data?.observations
      });
      this.configTable.data = bankAccounts;

      try {
        this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_other');
      } catch {
        this.fieldAuditData = {};
      }
    }
  }

  get bankAccounts(): FormArray {
    return this.bankAccountForm.get('bankAccounts') as FormArray;
  }

  addBankAccount() {
    const account = this.fb.group({
      id: [],
      bankName: [''],
      iban: [''],
      startDate: [],
      endDate: [],
      observations: [''],
      client: [],
    });

    this.bankAccounts.push(account);
  }

  async save() {
    this.otherDataForm.value.client = Number(this.clientId);
    if (!this.otherDataForm.value.id) {
      delete this.otherDataForm.value['id'];
    }
    await this.clientsService.saveClientOtherData(this.otherDataForm.value);

    for (let i = 0; i < this.bankAccounts.length; i++) {
      if(this.bankAccounts.dirty){
        if(!this.bankAccounts.at(i).value.client) {
          this.bankAccounts.at(i).value.client = Number(this.clientId);
        }
        if (!this.bankAccounts.at(i).value.id) {
          delete this.bankAccounts.value['id'];
        }
        await this.clientsService.saveClientBankAccount({
          ...this.bankAccounts.at(i).value,
          startDate: toDateOnlyPayload(this.bankAccounts.at(i).value.startDate),
          endDate: toDateOnlyPayload(this.bankAccounts.at(i).value.endDate),
        });
      }
    }
    this.bankAccounts.clear();
    await this.getData();
  }

  async reopen(event) {
    await this.setAccountEndDate(event, null);
  }

  async close(event) {
    // Mediodía local: la medianoche (new Date() a las 00:30) viajaría como el día anterior en UTC.
    await this.setAccountEndDate(event, toDateOnlyPayload(new Date()));
  }

  private async setAccountEndDate(index: number, endDate: Date | null) {
    const row = this.configTable.data[index];
    const control = (this.bankAccountForm.get('bankAccounts') as FormArray).at(index).get('endDate');
    const previous = row.endDate;
    row.endDate = endDate;
    control.setValue(endDate);
    const account = (this.bankAccountForm.get('bankAccounts') as FormArray).at(index).value;
    account.client = this.clientId;
    try {
      await this.clientsService.saveClientBankAccount({
        ...account,
        startDate: toDateOnlyPayload(account.startDate),
        endDate: toDateOnlyPayload(account.endDate),
      });
    } catch (error) {
      // El servidor no lo guardó: no mostrar la cuenta como cerrada/reabierta.
      row.endDate = previous;
      control.setValue(previous);
      throw error;
    }
  }

  openFormModal(index: number | null = null) {
    this.submitError = '';
    this.isModalActive = true;
    // El modo histórico deshabilita el formulario; al abrirlo de nuevo debe volver a ser editable.
    this.modalForm.enable({ emitEvent: false });
    if (index === null) {
      this.isCreating = true;
      this.modalForm.reset();
    } else {
      this.isCreating = false;
      const account = this.snapshotData
        ? this.configTable.data[index]
        : this.bankAccounts.at(index).value;
      this.modalForm.patchValue({ ...account, iban: this.formatIban(account.iban) });
      if (this.snapshotData) {
        this.modalForm.disable();
      } else if(!this.hasEditPermission) {
        Object.keys(this.modalForm.controls).forEach((control) => {
          this.modalForm.get(control)?.disable({ emitEvent: false });
        });
      }
    }
  }
  
  /** Guarda de envío: un doble clic crearía duplicados. */
  isSubmitting = false;
  /** Último error de guardado o borrado de un modal, para mostrarlo al usuario. */
  submitError = '';

  async saveItem() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doSaveItem();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSaveItem() {
    const index = this.bankAccounts.controls.findIndex(
      (control) => control.value.id === this.modalForm.value.id
    );
    if (index > -1) {
      this.bankAccounts.at(index).patchValue(this.modalForm.value);
      this.bankAccounts.at(index).value.client = Number(this.clientId);
      let account = this.bankAccounts.at(index).value;
      account.iban = this.onIbanInput(account.iban);
      await this.clientsService.saveClientBankAccount({
        ...account,
        startDate: toDateOnlyPayload(account.startDate),
        endDate: toDateOnlyPayload(account.endDate),
      });
    } else {
      this.modalForm.value.client = Number(this.clientId);
      this.modalForm.value.iban = this.onIbanInput(this.modalForm.value.iban) || null;
      this.bankAccounts.push(this.fb.group(this.modalForm.value));
      const newAccount = this.bankAccounts.at(this.bankAccounts.length - 1).value;
      await this.clientsService.saveClientBankAccount({
        ...newAccount,
        startDate: toDateOnlyPayload(newAccount.startDate),
        endDate: toDateOnlyPayload(newAccount.endDate),
      });
    }
    await this.getData();
    this.isModalActive = false;
  }

  /** Formato de guardado: sin espacios y en mayúsculas (alta y edición igual). */
  onIbanInput(input: string) {
    return normalizeIban(input);
  }

  formatIban(iban: string): string {
    return (iban ?? '').replace(/\W/g, '').replace(/(.{4})/g, '$1 ').trim();
  }

}
